import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { noop, size } from "lodash"
import Modal from "@mui/material/Modal"
import { ROLE } from "helpers/shapes"
import { LOADING } from "helpers/data-statuses"
import { useReadPreference } from "hooks/preferences"
import { useAccountId } from "resources/account"
import Login from "modules/login"
import { Outlet, useMatches, useNavigate } from "react-router-dom"
import { KellyChatGate } from "connected-components/agent-chat/chatbox/kelly-chat-context"
import { KellyDock } from "connected-components/agent-chat/chatbox/kelly-dock"
import { KellyDockAnalyticsProvider } from "connected-components/agent-chat/analytics/dock-provider"
import { resolveAccountRootLanding, LAST_LANDING_PREFERENCE_KEY, LAST_LANDING_PREFERENCE_TYPE } from "./landing-preference"
import { useSyncLastLandingFromRoute } from "./use-sync-last-landing-from-route"
import useDocumentTitle from "hooks/use-document-title"
import "./style.scss"

export default function AccountRoot({ loadAccount, accountAlias, selectPortfolio, sessionExpired, needSignTerms, isSuperuser, role, account, models, datasetEntitlementsEnabled }) {
  const [isLoaded, setIsLoaded] = useState(false)
  const navigate = useNavigate()
  const matches = useMatches()
  const atRootMatch = matches.length === 1
  const accountId = useAccountId()
  useDocumentTitle()
  const [lastLandingPreference, lastLandingPreferenceStatus] = useReadPreference(
    LAST_LANDING_PREFERENCE_KEY,
    LAST_LANDING_PREFERENCE_TYPE,
    null,
  )

  useEffect(() => {
    if (accountAlias) {
      loadAccount(accountAlias)
    }
  }, [accountAlias, loadAccount])

  useSyncLastLandingFromRoute(accountAlias, atRootMatch)

  useEffect(() => {
    if (!account) {
      return
    }
    setIsLoaded(true)
    if (needSignTerms && !isSuperuser) {
      let url = `/${accountAlias}/account-not-activated`
      if (role === "admin") {
        url = `/${accountAlias}/terms-and-conditions`
      }
      navigate(url, { replace: true })
    } else if (atRootMatch) { // we're at the root match
      // If you arrive at the root, redirect to the last tracked landing surface or to home on first visit.
      const preferenceAlignedWithRoute = accountAlias === account
      if (preferenceAlignedWithRoute && accountId && lastLandingPreferenceStatus === LOADING) {
        return
      }
      const { url, portfolioIdToSelect } = resolveAccountRootLanding({
        accountAlias,
        preferenceValue: preferenceAlignedWithRoute ? lastLandingPreference : null,
      })
      if (portfolioIdToSelect) {
        selectPortfolio(portfolioIdToSelect)
      }
      navigate(url, { replace: true })
    }
  }, [account, needSignTerms, isSuperuser, atRootMatch, role, accountAlias, selectPortfolio, navigate, accountId, lastLandingPreference, lastLandingPreferenceStatus])

  // Member with no datasets check
  useEffect(() => {
    if (isSuperuser || needSignTerms || !account) {
      return
    }
    if (datasetEntitlementsEnabled && !size(models) && role !== "admin") {
      navigate("/contact-admin?risk-model", { replace: true })
    }
  }, [accountAlias, models, role, navigate, account, needSignTerms, isSuperuser, datasetEntitlementsEnabled])

  // This is to prevent `children` from being rendered (and queries fired) before knowing if we need to redirect or not
  if (!isLoaded) {
    return null
  }

  return (<>
    <div className="modules_account-root">
      {/* The highest component that survives every in-account navigation — see ADR 0018. */}
      <KellyChatGate accountAlias={accountAlias}>
        <Outlet />
        {/* Inside the gate so the dock shares the controller's lifetime — and so the gate's error boundary,
            which renders children bare, takes the dock (and its width reservation) down with it (ADR 0019). */}
        {/* Around the dock and NOT around the Outlet: an AnalyticsContext above the Outlet would nest
            DashboardAnalyticsProvider inside it and give every dashboard event Kelly's prefixes (ADR 0023). */}
        <KellyDockAnalyticsProvider>
          <KellyDock />
        </KellyDockAnalyticsProvider>
      </KellyChatGate>
    </div>
    {sessionExpired && <Modal onClose={() => noop()} className="modules_account-root-sign-in" open>
      <div className="login-popup-container">
        <Login isRefreshMode />
      </div>
    </Modal>}
  </>
  )
}

AccountRoot.propTypes = {
  children: PropTypes.object,
  loadAccount: PropTypes.func.isRequired,
  selectPortfolio: PropTypes.func.isRequired,
  accountAlias: PropTypes.string.isRequired,
  portfolios: PropTypes.array,
  sessionExpired: PropTypes.bool.isRequired,
  needSignTerms: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool.isRequired,
  role: ROLE,
  account: PropTypes.string,
  models: PropTypes.array,
  datasetEntitlementsEnabled: PropTypes.bool.isRequired,
}
